<template>
  <div class="gps-benchmarks">
    <div class="bm-filters">
      <label>
        Session
        <select v-model="sessionType" @change="load">
          <option value="training">Training</option>
          <option value="game">Games</option>
          <option value="">Both</option>
        </select>
      </label>

      <label>
        Match day
        <select v-model="mdOffset" @change="load">
          <option value="all">All</option>
          <option v-for="b in mdBuckets" :key="b.md_offset" :value="b.md_offset">
            {{ mdLabel(b.md_offset) }} ({{ b.n }})
          </option>
        </select>
      </label>

      <label>
        Min sessions
        <select v-model.number="minSessions" @change="load">
          <option :value="1">1</option>
          <option :value="3">3</option>
          <option :value="5">5</option>
        </select>
      </label>

      <span v-if="squad && mode === 'leaderboard'" class="bm-context">
        {{ players.length }} players · squad average shown as the dashed line
      </span>

      <button
        v-if="mode === 'leaderboard'"
        class="bm-export"
        @click="exportPdf"
        :disabled="!players.length"
      >Export PDF</button>
      <button class="bm-export" @click="exportCsv" :disabled="!canExport">
        Export CSV
      </button>
    </div>

    <div class="bm-modes">
      <button :class="{ on: mode === 'leaderboard' }" @click="setMode('leaderboard')">Leaderboard</button>
      <button :class="{ on: mode === 'records' }" @click="setMode('records')">Squad records</button>
      <button :class="{ on: mode === 'ages' }" @click="setMode('ages')">By age group</button>
    </div>

    <div v-if="loading" class="bm-msg">Loading benchmarks…</div>
    <div v-else-if="error" class="bm-msg bm-error">{{ error }}</div>
    <div v-else-if="!players.length" class="bm-msg">
      No sessions match this filter.
      <template v-if="minSessions > 1">
        Players need at least {{ minSessions }} comparable sessions to appear —
        try lowering “Min sessions”.
      </template>
    </div>

    <template v-else-if="mode === 'records'">
      <p class="bm-context">The single best session ever recorded for each metric.</p>
      <div class="bm-records">
        <div v-for="(r, k) in records" :key="k" class="bm-record">
          <span class="bm-rec-label">{{ r.label }}</span>
          <span class="bm-rec-value">{{ fmt(r.value, k) }}<small v-if="r.unit"> {{ r.unit }}</small></span>
          <span class="bm-rec-who">{{ r.name }}</span>
          <span class="bm-rec-when">
            {{ formatDate(r.session_date) }} · {{ r.session_type }}
            <template v-if="r.md_offset !== null"> · {{ mdLabel(r.md_offset) }}</template>
          </span>
        </div>
      </div>
      <p v-if="!Object.keys(records).length" class="bm-msg">No records yet.</p>
    </template>

    <template v-else-if="mode === 'ages'">
      <p class="bm-context">
        Squad averages side by side — a younger group matching an older one's
        numbers is the clearest pathway signal there is.
      </p>
      <div class="bm-table-wrap">
        <table class="bm-ages">
          <thead>
            <tr>
              <th>Squad</th><th>Players</th>
              <th v-for="k in metricOrder" :key="k">{{ metrics[k] ? metrics[k].label : k }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="g in groups" :key="g.team_id">
              <td>{{ g.teamName || g.age_group }}</td>
              <td>{{ g.players }}</td>
              <td v-for="k in metricOrder" :key="k">{{ fmt(g[k], k) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="!groups.length" class="bm-msg">
        Not enough linked players per squad to compare age groups yet.
      </p>
    </template>

    <template v-else>
      <div class="bm-kpis" v-if="squad">
        <div v-for="k in metricOrder" :key="k" class="bm-kpi">
          <span class="bm-kpi-val">{{ fmt(squad[k], k) }}</span>
          <span class="bm-kpi-lbl">Squad avg {{ metrics[k].label.toLowerCase() }}</span>
        </div>
      </div>

      <div class="bm-charts">
        <div v-for="k in metricOrder" :key="k" class="bm-chart">
          <h4>{{ metrics[k].label }} <small v-if="metrics[k].unit">({{ metrics[k].unit }})</small></h4>
          <div class="bm-rows">
            <div
              v-for="p in ranked(k)"
              :key="p.player_id"
              class="bm-row"
              @click="$emit('select-player', p)"
            >
              <span class="bm-name">{{ p.name }}</span>
              <span class="bm-track">
                <span
                  class="bm-bar"
                  :style="{ width: pct(p[k], k) + '%', background: colour(p[k], k) }"
                ></span>
                <span
                  v-if="squad && squad[k]"
                  class="bm-avg"
                  :style="{ left: pct(squad[k], k) + '%' }"
                ></span>
              </span>
              <span class="bm-val">{{ fmt(p[k], k) }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { PlayerData } from "@/services/playerData-service.js";
import { buildGpsReportPdf } from "@/utils/gpsReportPdf.js";
import { getTenantName } from "@/config/tenant";

const ORDER = [
  "total_distance_m",
  "high_intensity_m",
  "sprint_distance_m",
  "accelerations",
  "top_speed_kph",
  "metres_per_minute",
];

export default {
  name: "GpsBenchmarks",
  props: {
    teamId: { type: [Number, String], default: null },
  },
  emits: ["select-player"],
  data() {
    return {
      loading: false,
      error: null,
      sessionType: "training",
      // MD-4 is the session academies load heaviest and compare most, so it is
      // the most useful thing to land on.
      mdOffset: "all",
      minSessions: 3,
      mode: "leaderboard",
      records: {},
      groups: [],
      players: [],
      squad: null,
      metrics: {},
      mdBuckets: [],
      metricOrder: ORDER,
    };
  },
  mounted() {
    this.load();
  },
  computed: {
    clubName() {
      try { return getTenantName() || "GPS report"; } catch (e) { return "GPS report"; }
    },
    canExport() {
      if (this.mode === "records") return Object.keys(this.records).length > 0;
      if (this.mode === "ages") return this.groups.length > 0;
      return this.players.length > 0;
    },
  },
  methods: {
    setMode(m) {
      this.mode = m;
      this.load();
    },

    /** The one-page report, laid out like the Tableau export it replaces. */
    exportPdf() {
      const bits = [];
      bits.push(this.sessionType === "game" ? "Games" : this.sessionType === "training" ? "Training" : "Games & training");
      if (this.mdOffset !== "all") bits.push(this.mdLabel(Number(this.mdOffset)));
      bits.push(`${this.players.length} players · min ${this.minSessions} sessions`);

      const doc = buildGpsReportPdf({
        clubName: this.clubName,
        subtitle: bits.join("  ·  "),
        players: this.players,
        squad: this.squad || {},
        metrics: this.metrics,
        metricOrder: this.metricOrder,
      });
      const md = this.mdOffset === "all" ? "all" : `MD${this.mdOffset}`;
      doc.save(`gps-report-${md}-${new Date().toISOString().slice(0, 10)}.pdf`);
    },

    /** CSV of whatever is on screen — this is what retires the Tableau export. */
    exportCsv() {
      let head = [];
      let rows = [];
      if (this.mode === "records") {
        head = ["Metric", "Value", "Unit", "Player", "Date", "Session"];
        rows = Object.entries(this.records).map(([, r]) => [
          r.label, r.value, r.unit, r.name, r.session_date, r.session_type,
        ]);
      } else if (this.mode === "ages") {
        head = ["Squad", "Players", ...this.metricOrder.map((k) => this.labelFor(k))];
        rows = this.groups.map((g) => [
          g.teamName || g.age_group, g.players, ...this.metricOrder.map((k) => g[k]),
        ]);
      } else {
        head = ["Player", "Sessions", ...this.metricOrder.map((k) => this.labelFor(k))];
        rows = this.players.map((p) => [
          p.name, p.sessions, ...this.metricOrder.map((k) => p[k]),
        ]);
      }
      const esc = (v) => {
        const t = v === null || v === undefined ? "" : String(v);
        return /[",\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t;
      };
      const csv = [head, ...rows].map((r) => r.map(esc).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `gps-${this.mode}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    },

    labelFor(k) {
      return (this.metrics[k] && this.metrics[k].label) || k;
    },

    formatDate(d) {
      try {
        return new Date(String(d).replace(" ", "T")).toLocaleDateString("en-GB", {
          day: "numeric", month: "short", year: "numeric",
        });
      } catch (e) { return d; }
    },

    async load() {
      this.loading = true;
      this.error = null;
      try {
        if (this.mode === "records") { await this.loadRecords(); return; }
        if (this.mode === "ages") { await this.loadGroups(); return; }
        const { data } = await PlayerData.find({
          action: "getGpsBenchmarks",
          sessionType: this.sessionType,
          mdOffset: this.mdOffset,
          minSessions: this.minSessions,
          teamId: this.teamId || undefined,
        });
        const d = (data && data.data) || data || {};
        if (!d.provider) {
          this.error = "No GPS data has been synced for this club yet.";
          this.players = [];
          return;
        }
        this.players = d.players || [];
        this.squad = d.squad || null;
        this.metrics = d.metrics || {};
        this.mdBuckets = d.mdBuckets || [];
      } catch (e) {
        this.error = "Could not load GPS benchmarks.";
      } finally {
        this.loading = false;
      }
    },

    async loadRecords() {
      const { data } = await PlayerData.find({
        action: "getGpsRecords",
        sessionType: this.sessionType,
        teamId: this.teamId || undefined,
      });
      const d = (data && data.data) || data || {};
      this.records = d.records || {};
      if (!Object.keys(this.metrics).length && d.metrics) this.metrics = d.metrics;
    },

    async loadGroups() {
      const { data } = await PlayerData.find({
        action: "getGpsByAgeGroup",
        sessionType: this.sessionType,
        mdOffset: this.mdOffset,
      });
      const d = (data && data.data) || data || {};
      this.groups = d.groups || [];
      if (d.metrics) this.metrics = d.metrics;
    },

    /** Players ranked on one metric, best first, blanks dropped. */
    ranked(key) {
      return [...this.players]
        .filter((p) => p[key] !== null && p[key] !== undefined && p[key] !== "")
        .sort((a, b) => Number(b[key]) - Number(a[key]));
    },

    max(key) {
      const vals = this.ranked(key).map((p) => Number(p[key]));
      return vals.length ? Math.max(...vals) : 0;
    },

    pct(v, key) {
      const m = this.max(key);
      if (!m || v == null) return 0;
      return Math.max(2, Math.min(100, (Number(v) / m) * 100));
    },

    /** Red through amber to green against the squad average, as the clubs' own reports read. */
    colour(v, key) {
      const avg = this.squad && this.squad[key] ? Number(this.squad[key]) : null;
      if (!avg || v == null) return "#6c757d";
      const r = Number(v) / avg;
      if (r >= 1.1) return "#198754";
      if (r >= 0.95) return "#7cb342";
      if (r >= 0.85) return "#f6aa1e";
      return "#dc3545";
    },

    fmt(v, key) {
      if (v === null || v === undefined || v === "") return "—";
      const n = Number(v);
      if (key === "top_speed_kph") return n.toFixed(1);
      if (key === "total_distance_m" && n >= 1000) return (n / 1000).toFixed(2) + "K";
      return Math.round(n).toLocaleString();
    },

    mdLabel(md) {
      const n = Number(md);
      if (n === 0) return "MD (match day)";
      return n < 0 ? `MD${n}` : `MD+${n}`;
    },
  },
};
</script>

<style scoped>
.gps-benchmarks { padding: 0.5rem 0; }

.bm-filters {
  display: flex; flex-wrap: wrap; gap: 1rem; align-items: flex-end;
  padding: 0.75rem 0 1rem;
}
.bm-filters label { display: flex; flex-direction: column; font-size: 0.75rem; color: #6c757d; gap: 0.25rem; }
.bm-filters select { padding: 0.4rem 0.6rem; border: 1px solid #ced4da; border-radius: 6px; font-size: 0.85rem; }
.bm-context { font-size: 0.78rem; color: #6c757d; padding-bottom: 0.4rem; }

.bm-export { margin-left: auto; border: 1px solid #ced4da; background: #fff; color: #495057;
  border-radius: 6px; padding: .35rem .8rem; font-size: .8rem; cursor: pointer; }
.bm-export:disabled { opacity: .45; cursor: default; }

.bm-modes { display: flex; gap: .4rem; padding: 0 0 1rem; }
.bm-modes button { border: none; background: none; color: #6c757d; font-size: .82rem;
  padding: .3rem .6rem; border-radius: 5px; cursor: pointer; }
.bm-modes button.on { background: #e9ecef; color: #212529; font-weight: 600; }

.bm-records { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: .85rem; }
.bm-record { background: #f8f9fa; border-radius: 10px; padding: .85rem; display: flex; flex-direction: column; gap: .15rem; }
.bm-rec-label { font-size: .68rem; text-transform: uppercase; letter-spacing: .04em; color: #868e96; }
.bm-rec-value { font-size: 1.35rem; font-weight: 700; color: #212529; }
.bm-rec-value small { font-size: .68rem; font-weight: 500; color: #868e96; }
.bm-rec-who { font-size: .85rem; color: #212529; font-weight: 600; }
.bm-rec-when { font-size: .7rem; color: #868e96; }

.bm-table-wrap { overflow-x: auto; }
.bm-ages { width: 100%; border-collapse: collapse; font-size: .82rem; }
.bm-ages th { text-align: left; font-size: .68rem; text-transform: uppercase; color: #868e96;
  padding: .4rem .55rem; border-bottom: 1px solid #dee2e6; white-space: nowrap; }
.bm-ages td { padding: .5rem .55rem; border-bottom: 1px solid #f1f3f5; white-space: nowrap; }
.bm-ages tbody tr:hover { background: #f8f9fa; }

.bm-msg { padding: 1.5rem 0; color: #6c757d; font-size: 0.9rem; }
.bm-error { color: #dc3545; }

.bm-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 0.75rem; margin-bottom: 1.5rem; }
.bm-kpi { background: #f8f9fa; border-radius: 8px; padding: 0.75rem; text-align: center; }
.bm-kpi-val { display: block; font-size: 1.25rem; font-weight: 700; color: #212529; }
.bm-kpi-lbl { display: block; font-size: 0.68rem; color: #6c757d; margin-top: 0.15rem; }

.bm-charts { display: grid; grid-template-columns: repeat(auto-fit, minmax(340px, 1fr)); gap: 1.75rem; }
.bm-chart h4 { font-size: 0.9rem; margin: 0 0 0.6rem; color: #212529; }
.bm-chart h4 small { color: #6c757d; font-weight: 400; }

.bm-row { display: grid; grid-template-columns: 8.5rem 1fr 3.5rem; align-items: center; gap: 0.5rem; padding: 2px 0; cursor: pointer; }
.bm-row:hover .bm-name { color: #0d6efd; }
.bm-name { font-size: 0.76rem; color: #495057; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bm-track { position: relative; height: 16px; background: #f1f3f5; border-radius: 3px; }
.bm-bar { display: block; height: 100%; border-radius: 3px; }
/* The dashed squad-average line, as on the club's existing report. */
.bm-avg { position: absolute; top: -2px; bottom: -2px; width: 0; border-left: 2px dashed #868e96; }
.bm-val { font-size: 0.76rem; text-align: right; color: #212529; font-variant-numeric: tabular-nums; }
</style>
